import logger from '../utils/logger';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { settingsService } from '../services/settingsService';
import { useSettings } from './SettingsContext';

const TimezoneContext = createContext();

export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};

export const TimezoneProvider = ({ children }) => {
  const { timezoneSettings, isLoading: settingsLoading } = useSettings();
  const [timezone, setTimezone] = useState('UTC');
  const [isLoading, setIsLoading] = useState(true);

  // Load timezone from centralized SettingsContext
  useEffect(() => {
    if (!settingsLoading && timezoneSettings) {
      if (timezoneSettings.timezone) {
        setTimezone(timezoneSettings.timezone);
      }
      setIsLoading(false);
    }
  }, [timezoneSettings, settingsLoading]);

  const updateTimezone = async (newTimezone) => {
    try {
      setTimezone(newTimezone);
      
      // Save to settings
      await settingsService.updateSettingsBulk('general', {
        timezone: newTimezone
      });
    } catch (error) {
      logger.error('Failed to save timezone setting:', error);
      // Revert timezone on error
      await loadTimezoneFromSettings();
    }
  };

  // Format date in user's timezone
  const formatDate = (date, options = {}) => {
    const dateObj = new Date(date);
    
    const defaultOptions = {
      timeZone: timezone,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...options
    };

    return new Intl.DateTimeFormat('en-US', defaultOptions).format(dateObj);
  };

  // Format time in user's timezone
  const formatTime = (date, options = {}) => {
    const dateObj = new Date(date);
    
    const defaultOptions = {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      ...options
    };

    return new Intl.DateTimeFormat('en-US', defaultOptions).format(dateObj);
  };

  // Get current time in user's timezone
  const getCurrentTime = () => {
    return formatTime(new Date());
  };

  // Get current date in user's timezone
  const getCurrentDate = () => {
    return formatDate(new Date(), { 
      hour: undefined, 
      minute: undefined 
    });
  };

  // Convert UTC time to user's timezone
  const fromUTC = (utcDate) => {
    return new Date(utcDate).toLocaleString('en-US', {
      timeZone: timezone
    });
  };

  // Convert user's timezone time to UTC
  const toUTC = (localDate) => {
    // Create date object assuming it's in user's timezone
    const date = new Date(localDate);
    
    // Get timezone offset in minutes
    const userTimezoneOffset = getTimezoneOffset(timezone);
    const localTimezoneOffset = date.getTimezoneOffset();
    
    // Calculate the difference and adjust
    const offsetDiff = (userTimezoneOffset - localTimezoneOffset) * 60000;
    return new Date(date.getTime() + offsetDiff);
  };

  // Get relative time string (e.g., "2 hours ago")
  const getRelativeTime = (date) => {
    const now = new Date();
    const past = new Date(date);
    const diffInSeconds = Math.floor((now - past) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    } else {
      return formatDate(date, { 
        hour: undefined, 
        minute: undefined 
      });
    }
  };

  // Get timezone info
  const getTimezoneInfo = () => {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'long'
    });
    
    const parts = formatter.formatToParts(now);
    const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value;
    
    return {
      timezone: timezone,
      name: timeZoneName,
      offset: getTimezoneOffset(timezone),
      abbreviation: getTimezoneAbbreviation(timezone)
    };
  };

  // Get timezone offset in minutes
  const getTimezoneOffset = (tz) => {
    const now = new Date();
    const utc = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
    const local = new Date(utc.toLocaleString('en-US', { timeZone: tz }));
    return (local.getTime() - utc.getTime()) / 60000;
  };

  // Get timezone abbreviation
  const getTimezoneAbbreviation = (tz) => {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short'
    });
    
    const parts = formatter.formatToParts(new Date());
    return parts.find(part => part.type === 'timeZoneName')?.value || tz;
  };

  // Format duration in user's preferred format
  const formatDuration = (milliseconds) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days}d ${hours % 24}h ${minutes % 60}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const value = {
    timezone,
    setTimezone: updateTimezone,
    isLoading,
    formatDate,
    formatTime,
    getCurrentTime,
    getCurrentDate,
    fromUTC,
    toUTC,
    getRelativeTime,
    getTimezoneInfo,
    formatDuration
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};

export default TimezoneContext;